import React, { useRef, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import css from './HomeHowItWorkSection.module.css';
import IconCard from '../../../../../components/SavedCardDetails/IconCard/IconCard';
import workImage1 from '../../../../../assets/work-image-1.png';
import workImage2 from '../../../../../assets/work-image-2.png';
import { Button } from '../../../../../components';
import { FormattedMessage } from '../../../../../util/reactIntl';

const workData = [
  {
    cardNumber: '01',
    cardNumberColor: '#056C9E',
    titleKey: 'HomeHowItWorkSection.card1.title',
    descriptionKey: 'HomeHowItWorkSection.card1.description',
    buttonNameKey: 'HomeHowItWorkSection.card1.button',
    buttonLink: '/l/new',
    image: workImage1,
    color: '#056C9E',
  },
  {
    cardNumber: '02',
    cardNumberColor: '#F16319',
    titleKey: 'HomeHowItWorkSection.card2.title',
    descriptionKey: 'HomeHowItWorkSection.card2.description',
    buttonNameKey: 'HomeHowItWorkSection.card2.button',
    buttonLink: '/s',
    image: workImage2,
    color: '#F16319',
  },
];

function HomeHowItWorkSection() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-20%' });

  // Disable animations for narrow screens
  const [disableAnim, setDisableAnim] = React.useState(false);
  useEffect(() => {
    const check = () => setDisableAnim(window.innerWidth <= 1320);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (window.location.hash === '#how-it-works') {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  return (
    <div id="how-it-works" className={css.howItWorkSectionWrapper} ref={sectionRef}>
      <div className={css.howItWorkSectionContainer}>
        {/* LEFT CONTENT */}
        <motion.div
          className={css.howItWorkSectionContenDetail}
          initial={disableAnim ? {} : { x: 0, display: 'block' }}
          animate={disableAnim ? {} : isInView ? { x: -300, opacity: 0, display: 'none' } : {}}
          transition={disableAnim ? undefined : { duration: 0.8, ease: 'easeInOut' }}
        >
          <div className={css.workBadge}>
            <IconCard type="workbadge" />
          </div>
          <h5>
            <FormattedMessage id="HomeHowItWorkSection.title" />
          </h5>
          <h1>
            <FormattedMessage
              id="HomeHowItWorkSection.subTitle"
              values={{
                highlightedText: (
                  <span>
                    <FormattedMessage id="HomeHowItWorkSection.subTitleHighlight" />
                  </span>
                ),
              }}
            />
          </h1>
        </motion.div>

        {/* RIGHT CARDS */}
        <div className={css.cardsWrapper}>
          {disableAnim ? (
            <>
              {workData.map((item, index) => (
                <div
                  key={`${item.title}-${index}`}
                  className={css.workCard}
                  style={{ backgroundColor: item.color }}
                >
                  <div className={css.workCardContent}>
                    <div className={css.workCardNumber} style={{ color: item.cardNumberColor }}>
                      {item.cardNumber}
                    </div>
                    <div className={css.bottomContent}>
                      <h5>
                        <FormattedMessage id={item.titleKey} />
                      </h5>
                      <p>
                        <FormattedMessage id={item.descriptionKey} />
                      </p>
                      <Button onClick={() => (window.location.href = item.buttonLink)}>
                        <FormattedMessage id={item.buttonNameKey} />
                      </Button>
                    </div>
                  </div>
                  <div className={css.imageWrapper}>
                    <img src={item.image} alt="" loading="lazy" decoding="async" />
                  </div>
                </div>
              ))}
            </>
          ) : (
            <AnimatePresence>
              {isInView &&
                workData.map((item, index) => (
                  <motion.div
                    key={item.titleKey}
                    className={css.workCard}
                    style={{
                      backgroundColor: item.color,
                    }}
                    initial={{ x: 300, opacity: 1 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -300, opacity: 0 }}
                    transition={{
                      duration: 0.8,
                      delay: 0.8 + index * 0.5,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  >
                    <div className={css.workCardContent}>
                      <div className={css.workCardNumber} style={{ color: item.cardNumberColor }}>
                        {item.cardNumber}
                      </div>
                      <div className={css.bottomContent}>
                        <h5>
                          <FormattedMessage id={item.titleKey} />
                        </h5>
                        <p>
                          <FormattedMessage id={item.descriptionKey} />
                        </p>
                        <Button onClick={() => (window.location.href = item.buttonLink)}>
                          <FormattedMessage id={item.buttonNameKey} />
                        </Button>
                      </div>
                    </div>
                    <div className={css.imageWrapper}>
                      <img src={item.image} alt="" loading="lazy" decoding="async" />
                    </div>
                  </motion.div>
                ))}
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
}

export default HomeHowItWorkSection;
